import type { MaybeRefOrGetter } from 'vue'
import type { Thing, WithContext } from 'schema-dts'

export type SeoOpenGraphType = 'website' | 'profile' | 'article'

export interface SeoImage {
  url: string
  alt?: string
  width?: number
  height?: number
  type?: string
}

export interface SeoRobots {
  index: boolean
  follow: boolean
  noarchive?: boolean
  nosnippet?: boolean
  maxSnippet?: number
  maxImagePreview?: 'none' | 'standard' | 'large'
  maxVideoPreview?: number
}

export const INDEX_FOLLOW: SeoRobots = { index: true, follow: true }
export const NOINDEX_FOLLOW: SeoRobots = { index: false, follow: true }
export const NOINDEX_NOFOLLOW: SeoRobots = { index: false, follow: false }

/**
 * Canonical URL for a page.
 *
 * - `'/pricing'` — relative path (optional `?query`), joined to the profile `baseUrl`
 * - `'https://example.com/pricing'` — absolute URL, kept as-is
 * - `{ url, type }` — the same two cases stated explicitly
 *
 * `http://` is coerced to `https://`, and any `#fragment` is dropped.
 */
export type SeoCanonical = string | { url: string; type: 'relative' | 'absolute' }

export interface SeoHeadLink {
  rel: string
  /** Relative path (optional `?query`) or absolute https URL — resolved like canonical. */
  href: string
}

export interface SeoDraft {
  title?: string
  description?: string
  image?: SeoImage
  canonical?: SeoCanonical
  ogType?: SeoOpenGraphType
  twitterCreator?: string
  robots?: SeoRobots
  jsonLd?: WithContext<Thing> | WithContext<Thing>[]
  /** Extra `<link>` tags (e.g. rel=prev|next). Emitted after canonical; `rel="canonical"` is ignored here. */
  headLinks?: SeoHeadLink[]
}

export interface SeoPolicyDecision {
  robots: SeoRobots
  reason: string
}

export type SeoFactory<TEntity> = (entity: TEntity) => SeoDraft
export type SeoPolicy<TEntity> = (entity: TEntity) => SeoPolicyDecision

export interface SeoEntityInput<TEntity> {
  source: MaybeRefOrGetter<TEntity | null | undefined>
  factory: SeoFactory<TEntity>
  policy?: SeoPolicy<TEntity>
}

export type SeoInput<TEntity> = MaybeRefOrGetter<SeoDraft> | SeoEntityInput<TEntity>

export interface SeoSiteProfile {
  siteName: string
  baseUrl: string
  defaultDescription: string
  defaultImage: SeoImage
  defaultRobots: SeoRobots
  defaultOgType: SeoOpenGraphType
  defaultTwitterCard: 'summary' | 'summary_large_image'
  formatTitle: (pageTitle?: string) => string
  twitterSite?: string
}

/** Internal resolved head payload (not part of the public draft API). */
export interface ResolvedSeo {
  title: string
  description: string
  image: SeoImage
  canonicalUrl: string
  robots: string
  ogType: SeoOpenGraphType
  siteName: string
  twitterCard: 'summary' | 'summary_large_image'
  twitterSite?: string
  twitterCreator?: string
  jsonLd?: SeoDraft['jsonLd']
  headLinks: SeoHeadLink[]
  reason?: string
}
